import { Button, ThemeToggle } from '@servicetim/ui/components';
import { cn, hasTenantRole, useConfig, useSession, useSessionClaims } from '@servicetim/ui/lib';
import { Link, useNavigate } from '@tanstack/react-router';
import { LogOut, Menu, ShieldOff, X } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { HealthBadge } from '@/components/health-badge';
import { NotificationBell } from '@/components/layout/notification-bell';
import { APP_NAME, NAV_SECTIONS, SURFACE_LABEL } from '@/routes/nav';

/** Sidebar navigation + top bar. Wraps every authenticated page. */
export function AppShell({ children }: { children: ReactNode }) {
  const config = useConfig();
  const session = useSession();
  const { tenantRole } = useSessionClaims();
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useState(false);
  const closeNav = () => setNavOpen(false);

  const userLabel = session.user?.email ?? session.user?.name ?? 'Signed in';
  const sections = NAV_SECTIONS.filter(
    (section) => section.minRole === undefined || hasTenantRole(tenantRole, section.minRole),
  );

  return (
    <div className="flex min-h-dvh">
      {navOpen ? (
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={closeNav}
        />
      ) : null}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-border border-r bg-surface-raised transition-transform',
          'md:static md:translate-x-0',
          navOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-14 items-center gap-2 border-border border-b px-4">
          <span
            aria-hidden="true"
            className="grid size-8 place-items-center rounded-md bg-brand-600 font-bold text-sm text-white"
          >
            S
          </span>
          <div className="flex min-w-0 flex-col leading-tight">
            <span className="truncate font-semibold text-fg">{APP_NAME}</span>
            <span className="text-fg-muted text-xs">{SURFACE_LABEL}</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto md:hidden"
            aria-label="Close navigation"
            onClick={closeNav}
          >
            <X />
          </Button>
        </div>

        <nav aria-label="Primary" className="flex-1 space-y-4 overflow-y-auto p-3">
          {sections.map((section) => (
            <div key={section.title} className="space-y-1">
              <p className="px-3 pt-1 font-semibold text-[11px] text-fg-muted uppercase tracking-wider">
                {section.title}
              </p>
              {section.items.map(({ label, icon: Icon, ...link }) => (
                <Link
                  key={link.to}
                  {...link}
                  onClick={closeNav}
                  activeProps={{ 'aria-current': 'page' }}
                  className={cn(
                    'flex items-center gap-3 rounded-md border-l-2 border-transparent px-2.5 py-1.5 font-medium text-fg-muted text-sm transition-colors',
                    'hover:bg-surface-sunken hover:text-fg',
                    'data-[status=active]:border-l-accent data-[status=active]:bg-accent-soft data-[status=active]:text-accent',
                  )}
                >
                  <Icon className="size-4" aria-hidden="true" />
                  {label}
                </Link>
              ))}
            </div>
          ))}
        </nav>

        <dl className="space-y-1 border-border border-t p-4 text-fg-muted text-xs">
          <div className="flex justify-between gap-2">
            <dt>Environment</dt>
            <dd className="font-medium text-fg-muted">{config.environment}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt>Role</dt>
            <dd className="font-medium text-fg-muted">{tenantRole}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt>API</dt>
            <dd className="truncate font-mono text-fg-muted">{config.apiBaseUrl}</dd>
          </div>
        </dl>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {session.authEnabled ? null : <AuthDisabledBanner devClaims={session.devClaims !== null} />}

        <header className="flex h-14 items-center gap-3 border-border border-b bg-surface-raised px-4">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label="Open navigation"
            onClick={() => setNavOpen(true)}
          >
            <Menu />
          </Button>
          <div className="truncate font-medium text-fg-muted text-sm">
            {APP_NAME} {SURFACE_LABEL}
          </div>
          <EnvironmentPill environment={config.environment} />
          <div className="ml-auto flex items-center gap-3">
            <NotificationBell />
            <HealthBadge />
            <ThemeToggle />
            <span
              className="hidden max-w-56 truncate text-fg-muted text-sm sm:inline"
              title={userLabel}
            >
              {userLabel}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void navigate({ to: '/signed-out' })}
            >
              <LogOut />
              Sign out
            </Button>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-8">{children}</main>
      </div>
    </div>
  );
}

function EnvironmentPill({ environment }: { environment: string }) {
  const isProd = environment === 'prod' || environment === 'production';
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 font-medium text-xs uppercase tracking-wide',
        isProd
          ? 'bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-300'
          : 'bg-surface-sunken text-fg-muted',
      )}
    >
      {environment}
    </span>
  );
}

function AuthDisabledBanner({ devClaims }: { devClaims: boolean }) {
  return (
    <div
      role="status"
      className="flex items-center gap-2 border-amber-200 dark:border-amber-500/30 border-b bg-amber-50 dark:bg-amber-500/15 px-4 py-2 text-amber-900 dark:text-amber-300 text-sm"
    >
      <ShieldOff className="size-4 shrink-0" aria-hidden="true" />
      {devClaims ? (
        <span>
          <strong className="font-semibold">Dev-claims session.</strong> Requests carry{' '}
          <code className="font-mono">x-dev-claims</code> from{' '}
          <code className="font-mono">config.json</code>; the API only honors it with{' '}
          <code className="font-mono">ALLOW_DEV_CLAIMS=true</code>.
        </span>
      ) : (
        <span>
          <strong className="font-semibold">Auth disabled: local development mode.</strong> Requests
          are sent without an Authorization header. Set <code className="font-mono">cognito</code>{' '}
          or <code className="font-mono">devClaims</code> in{' '}
          <code className="font-mono">config.json</code> to act as a user.
        </span>
      )}
    </div>
  );
}
